<script setup>
import { inject, onMounted, onUnmounted, shallowRef, useTemplateRef } from "vue";
import BreadcrumbBar from "~/components/BreadcrumbBar.vue";
import { iconHref } from "~/utils/icons.js";

/**
 * Common page layout shell providing the outer page background, About
 * dropdown header, and a rounded content box with breadcrumbs.
 *
 * @prop {Array<{ text: String, href?: String }>} breadcrumbs — forwarded to BreadcrumbBar
 * @slot default — page-specific content rendered inside the content box, below the breadcrumbs
 */
defineProps({
  breadcrumbs: { default: () => [], type: Array }
});

const isAdmin = inject("isAdmin", false);

const aboutOpen = shallowRef(false);
const aboutDropdownRef = useTemplateRef("aboutDropdown");
const appVersion = inject("appVersion", "");
const anonymousAccess = inject("anonymousAccess", false);
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
const csrfParam = document.querySelector('meta[name="csrf-param"]')?.content;

function toggleAbout() {
  aboutOpen.value = !aboutOpen.value;
}

function onClickOutside(event) {
  if (aboutDropdownRef.value && !aboutDropdownRef.value.contains(event.target)) {
    aboutOpen.value = false;
  }
}

onMounted(() => document.addEventListener("click", onClickOutside));
onUnmounted(() => document.removeEventListener("click", onClickOutside));
</script>

<template>
  <div class="flex h-screen flex-col bg-[var(--gl-background-color-subtle)]">
    <!-- About dropdown header -->
    <header class="bg-[var(--gl-background-color-subtle)]">
      <div class="flex items-center justify-end px-6 pt-2">
        <div ref="aboutDropdown" class="relative">
          <button
            type="button"
            :aria-expanded="aboutOpen"
            aria-haspopup="true"
            class="flex items-center gap-1 rounded-[var(--gl-action-border-radius)] px-3 py-1.5 text-sm text-[var(--gl-text-color-subtle)] hover:bg-[var(--gl-background-color-default)] hover:text-[var(--gl-text-color-default)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)]"
            @click.stop="toggleAbout"
          >
            About
            <svg :class="['h-3.5 w-3.5 transition-transform', aboutOpen && 'rotate-180']" aria-hidden="true">
              <use :href="iconHref('chevron-down')" />
            </svg>
          </button>

          <ul
            v-show="aboutOpen"
            class="absolute right-0 top-full z-10 mt-1 min-w-48 rounded border border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] py-1 shadow-[var(--gl-shadow-md)]"
          >
            <li v-if="appVersion">
              <span class="block px-4 py-2 text-xs font-semibold text-[var(--gl-text-color-subtle)]"
                >v{{ appVersion }}</span
              >
            </li>
            <li v-if="appVersion" aria-hidden="true">
              <hr class="my-1 border-[var(--gl-border-color-default)]" />
            </li>
            <li>
              <a
                href="https://dependabot-gitlab.gitlab.io/dependabot"
                target="_blank"
                rel="noopener noreferrer"
                class="block px-4 py-2 text-sm text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)] hover:no-underline"
              >
                Documentation
              </a>
            </li>
            <li v-if="isAdmin">
              <a
                href="/jobs"
                class="block px-4 py-2 text-sm text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)] hover:no-underline"
              >
                Jobs UI
              </a>
            </li>
            <li v-if="!anonymousAccess" aria-hidden="true">
              <hr class="my-1 border-[var(--gl-border-color-default)]" />
            </li>
            <li v-if="!anonymousAccess">
              <form method="post" action="/logout">
                <input type="hidden" name="_method" value="delete" />
                <input v-if="csrfParam" type="hidden" :name="csrfParam" :value="csrfToken" />
                <button
                  type="submit"
                  class="block w-full px-4 py-2 text-left text-sm text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)]"
                >
                  Logout
                </button>
              </form>
            </li>
          </ul>
        </div>
      </div>
    </header>

    <main class="flex min-h-0 w-full flex-1 flex-col px-6 py-3">
      <div
        class="flex min-h-0 flex-1 flex-col rounded-xl border border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)]"
      >
        <BreadcrumbBar :breadcrumbs="breadcrumbs" />
        <div class="flex-1 overflow-y-auto px-5 py-5">
          <slot />
        </div>
      </div>
    </main>
  </div>
</template>
